import jwtDecode from 'jwt-decode';

import config from 'config';
import Radium from "radium";

import * as XLSX from 'xlsx';
import {saveAs} from 'file-saver';

export const myDarkBlue = 'rgb(4, 62, 92)'; // #043E5C
export const myBlue = 'rgb(61, 86, 143)';

export const silverInfoStyles = {
    info: {
        fontSize: '16px',
        lineHeight: '20px',
        fontWeight: 'inherit',
        color: '#A4ADC1',
        marginLeft: '20px',
        userSelect: 'text',
        WebkitUserSelect: 'text',
        MozUserSelect: 'text',
        msUserSelect: 'text',
        cursor: 'text'
    }
}

export const filterInputStyle = {
    padding: '10px',
    borderRadius: '8px',
    border: '1px solid #ccc',
    width: 'calc(25% - 20px)',
    minWidth: '200px',
    backgroundColor: '#fff',
    boxShadow: 'inset 0 1px 3px rgba(0, 0, 0, 0.05)',
    fontSize: '14px'
};

export const titleStyles = {
    title: {
        color: myBlue,
        marginBottom: '20px',
        fontSize: '1.5rem',
    },
}

export const inputStyles = {
    input: {
        padding: '12px 16px',
        fontSize: '16px',
        width: '100%',
        borderRadius: '10px',
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: '#ddd',
        backgroundColor: '#fff',
        color: '#333',
        outline: 'none',
        transition: 'all 0.3s ease-in-out',
    },
    inputFocus: {
        boxShadow: '0 0 0 2px rgba(43, 123, 255, 0.2)',
    },
};

export const tabButtonStyles = {
    tabButtons: {
        marginTop: '25px',
        display: 'flex',
        gap: '10px',
        marginBottom: '30px',
        justifyContent: 'center',
    },
    tabButton: (isActive, activeBackground = '#043e5c') => ({
        padding: '10px 15px',
        border: 'none',
        background: isActive ? activeBackground : '#fff',
        boxShadow: '0 0 10px rgba(0, 0, 0, 0.2)',
        color: isActive ? '#fff' : '#333',
        cursor: 'pointer',
        fontWeight: 600,
        borderRadius: '10px',
        transition: 'background 0.3s',
        fontSize: '11px',
        position: 'relative',
    }),
    tabButtonBackgroundSilver: (isActive) => ({
        padding: '10px 15px',
        border: 'none',
        background: isActive ? '#043e5c' : 'rgb(235, 236, 240)',
        color: isActive ? '#fff' : '#333',
        cursor: 'pointer',
        fontWeight: 600,
        borderRadius: '10px',
        transition: 'background 0.3s',
        fontSize: '11px',
        position: 'relative',
    }),
}

export const MessageType = {
    quickReply: 'QUICK_REPLY',
    aiText: 'AI_TEXT',
    text: 'TEXT',
};

export function calculateRank({ratingPercent, avrgAnswerSpeed, answeredChatsCount}) {
    const rating = Math.min(100, ratingPercent);
    const chatsCount = answeredChatsCount;

    const ranks = [
        {
            name: '10',
            minRating: 100,
            maxAnswerSpeed: 10,
            minChats: 10000
        },
        {
            name: '9',
            minRating: 95,
            maxAnswerSpeed: 25,
            minChats: 5000
        },
        {
            name: '8',
            minRating: 90,
            maxAnswerSpeed: 35,
            minChats: 2700
        },
        {
            name: '7',
            minRating: 88,
            maxAnswerSpeed: 40,
            minChats: 2000
        },
        {
            name: '6',
            minRating: 87,
            maxAnswerSpeed: 45,
            minChats: 1300
        },
        {
            name: '5',
            minRating: 85,
            maxAnswerSpeed: 60,
            minChats: 800
        },
        {
            name: '4',
            minRating: 80,
            maxAnswerSpeed: 120,
            minChats: 500
        },
        {
            name: '3',
            minRating: 70,
            maxAnswerSpeed: 200,
            minChats: 300
        },
        {
            name: '2',
            minRating: 60,
            maxAnswerSpeed: 300,
            minChats: 100
        },
        {
            name: '1',
            minRating: 0,
            maxAnswerSpeed: 300,
            minChats: 0
        }
    ];

    for (const rank of ranks) {
        if (
            rating >= rank.minRating &&
            avrgAnswerSpeed <= rank.maxAnswerSpeed &&
            chatsCount >= rank.minChats
        ) {
            return rank.name;
        }
    }

    return '1';
}

export const globalExportToExcel = ({data, columns, fileName = 'export.xlsx'}) => {
    if (!data || !columns) return;

    const formattedData = data.map(row => {
        const formattedRow = {};
        columns.forEach(col => {
            const value = typeof col.value === 'function' ? col.value(row) : row[col.value];
            formattedRow[col.label] = value;
        });
        return formattedRow;
    });

    const ws = XLSX.utils.json_to_sheet(formattedData);

    const colWidths = columns.map(col => {
        const header = col.label;
        const maxLen = formattedData.reduce((max, row) => {
            const cell = row[header];
            return Math.max(max, cell ? cell.toString().length : 0);
        }, header.length);
        return {wch: maxLen + 2};
    });
    ws['!cols'] = colWidths;

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
    const wbout = XLSX.write(wb, {bookType: 'xlsx', type: 'array'});
    saveAs(new Blob([wbout], {type: 'application/octet-stream'}), fileName);
};

export function filterElements(elements, filterString, additionalPredicate = () => false) {
    const filter = filterString.toLowerCase();

    return elements.filter((elem) => {
        const elemValues = Object.values(elem);
        let matches = false;
        elemValues.forEach((value) => {
            value = value ? value.toString().toLowerCase() : '';
            matches = value.includes(filter) || matches;
        });
        return matches || additionalPredicate(elem);
    });
}

export function jsUcfirst(string) {
    try {
        return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
    } catch (e) {
        return '';
    }
}

export function validateEmail(email) {
    const error = {};
    let re = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    if (!email) {
        error.email = {};
        error.email.message = "Email must not be empty";
    } else if (!re.test(email.toLowerCase())) {
        error.email = {};
        error.email.message = "Please enter a valid email address";
    }
    return error;
}

export function sameDay(date1, date2) {
    return date1.getFullYear() === date2.getFullYear()
        && date1.getMonth() === date2.getMonth()
        && date1.getDate() === date2.getDate();
}

export function convertFormBackendDate(date) {
    return new Date(date);
}

export function convertToBackendDate(date) {
    return Math.floor(date.getTime());
}

export function chunkArray(array = [], size) {
    const result = [];
    for (let i = 0; i < array.length; i += size) result.push(array.slice(i, i + size));
    return result;
}

export function validJWT(token) {
    try {
        const tokenData = jwtDecode(token);
        return Date.now() < tokenData.exp * 1000;
    } catch (e) {
        return false;
    }
}

export function getMemberRole(user) {
    const {authorities} = user;
    const {roles} = config;
    return roles.find(role => role.id === authorities[0]);
}

export function getMemberRoleId(user) {
    return getMemberRole(user).id;
}

export function hasChatPermission(user) {
    return ((getMemberRoleId(user) === config.rolesMap.agent) || (getMemberRoleId(user) === config.rolesMap.owner) || (getMemberRoleId(user) === config.rolesMap.admin));
}

export function hasBillingPermission(user) {
    return getMemberRoleId(user) === config.rolesMap.owner;
}

export function hasManagementPermission(user) {
    const memberRoleId = getMemberRoleId(user);
    return memberRoleId === config.rolesMap.admin || memberRoleId === config.rolesMap.owner;
}

export function getPermissions(user) {
    const permissions = [];
    if (hasManagementPermission(user)) {
        permissions.push(config.permissions.management);
    }

    if (hasBillingPermission(user)) {
        permissions.push(config.permissions.billing);
    }

    if (hasChatPermission(user)) {
        permissions.push(config.permissions.chat);
    }

    return permissions;
}

export function getUserClients(user, company) {
    return company.subs.filter(sub => user.subCompanyIds.includes(sub.id));
}

export function memberSupportsClient(member, client) {
    return member.subCompanyIds.includes(client.id);
}

export function updateMemberRole(member, role) {
    return {id: member.id, authorities: [role.id]};
}

export function isPositiveInteger(str) {
    const isPositive = /^(0|[1-9]\d*)$/.test(str);
    if (!isPositive) {
        throw new Error('Not A Positive Number');
    }
}

export function isPositiveNonZeroInteger(str) {
    const isPositive = /^([1-9]\d*)$/.test(str);
    if (!isPositive) {
        throw new Error('Not A Positive Number');
    }
}

function componentToHex(c) {
    const hex = c.toString(16);
    return hex.length === 1 ? `0${hex}` : hex;
}

export function rgbToHex(r, g, b) {
    return `#${componentToHex(r)}${componentToHex(g)}${componentToHex(b)}`.toUpperCase();
}

export function hexToRgb(hex) {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
    } : null;
}

export function debounce(func, wait, immediate) {
    let timeout;
    return function (...args) {
        const context = this;
        const later = function () {
            timeout = null;
            if (!immediate) func.apply(context, args);
        };
        const callNow = immediate && !timeout;
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
        if (callNow) func.apply(context, args);
    };
}

export function mergeObjectWithId(idKey, values) {
    return values.find(value => value.id === idKey);
}

export function filterByStatus(data, criteria) {
    return data.filter(element => element.status === criteria);
}

export function returnIDs(dataArray) {
    const result = [];

    dataArray.forEach((element) => {
        result.push(element.id);
    });
    result.push(-1)

    return result;
}

export function returnIDsWithoutPushing(dataArray) {
    const result = [];

    dataArray.forEach((element) => {
        result.push(element.id);
    });

    return result;
}

export function dummyTrue() {
    return true;
}

export function isNumeric(n) {
    return !isNaN(parseFloat(n)) && isFinite(n);
}

export function configuredRadium(component) {
    return Radium({
        plugins: [
            Radium.Plugins.mergeStyleArray,
            Radium.Plugins.checkProps,
            Radium.Plugins.resolveMediaQueries,
            Radium.Plugins.resolveInteractionStyles,
            Radium.Plugins.keyframes,
            Radium.Plugins.visited,
            Radium.Plugins.removeNestedStyles,
            // Radium.Plugins.prefix,
            Radium.Plugins.checkProps,
        ],
    })(component);
}

export function formatSecondsToHHMMSS(totalSeconds) {
    if (totalSeconds == null) return 'N/A';

    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    return [hours, minutes, seconds]
        .map(unit => String(unit).padStart(2, '0'))
        .join(':');
}

export const getTodayDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = ('0' + (today.getMonth() + 1)).slice(-2);
    const day = ('0' + today.getDate()).slice(-2);
    return `${year}-${month}-${day}`;
};

export const getTomorrowDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const year = tomorrow.getFullYear();
    const month = ('0' + (tomorrow.getMonth() + 1)).slice(-2);
    const day = ('0' + tomorrow.getDate()).slice(-2);
    return `${year}-${month}-${day}`;
};

export const toLocalDateString = (date) => {
    const d = new Date(date);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};


// WEBPACK FOOTER //
// ./src/utils/General.js